import { Component, EventEmitter, inject, OnDestroy, OnInit, Output } from '@angular/core'
import { NgbModal } from '@ng-bootstrap/ng-bootstrap'
import { Store } from '@ngrx/store'
import { combineLatest, filter, map, Observable, Subject, take, takeUntil, tap } from 'rxjs'
import { KeySettingsComponent } from '../key-settings/key-settings.component'
import { environment } from '../../../../environments/environment'
import { eps } from '../../../services/url.service'
import {
  ContractorPayrollStatus,
  IncomingContractorPayroll,
  IncomingRunningPayrollBatch
} from '../../../store/contractor-payroll/contractor-payroll.state'
import { selectRunningPayroll } from '../../../store/contractor-payroll/contractor-payroll.reducers'
import { loadBusinesses } from '../../../store/business/business.actions'
import { runningCPActions } from '../../../store/contractor-payroll/contractor-payroll.actions'
import { selectBusinesses } from '../../../store/business/business.reducer'
import { CommonModule, NgFor } from '@angular/common'
import { RouterModule } from '@angular/router'
import { CapitalizePipe } from '../../../pipes/capitalize.pipe'
import { InvoiceComponent } from '../invoice/invoice.component'

@Component({
  selector: 'app-review',
  imports: [NgFor, RouterModule, CommonModule, CapitalizePipe],
  templateUrl: './review.component.html',
  styleUrl: './review.component.scss'
})
export class ReviewComponent implements OnInit, OnDestroy {
  private store = inject(Store)
  private modalService = inject(NgbModal)
  @Output() nextScreen = new EventEmitter<boolean>()
  private destroy$ = new Subject<void>()
  KeySettingsComponent = KeySettingsComponent
  InvoiceComponent = InvoiceComponent
  imageBaseUrl = environment.apiBaseUrl + '/uploads/'
  eps = eps
  PayrollStatus = ContractorPayrollStatus
  runningPayroll$ = this.store.select(selectRunningPayroll)
  businessEntity$!: Observable<string | undefined>
  currentIncomingPayrolls: IncomingContractorPayroll[] = []
  payPeriodStartDate = ''
  payPeriodEndDate = ''
  //currentPage = 1
  //pageLimit = 10
  //totalCount = 0
  //pageLastRecord = 0
  canConfirm = this.currentIncomingPayrolls.every(p => !!p.id)
  changeScreen(value: boolean) {
    this.nextScreen.emit(value)
  }

  ngOnInit(): void {
    this.store.dispatch(loadBusinesses({}))
    this.initializeBusinessEntityObservable()

    this.runningPayroll$
      .pipe(
        filter(
          (runningPayroll): runningPayroll is IncomingRunningPayrollBatch =>
            // Type guard
            !!runningPayroll &&
            !!runningPayroll.payPeriodStart &&
            !!runningPayroll.payPeriodEnd &&
            !!runningPayroll.belongsTo &&
            !!runningPayroll.payoutFrequency
        ),
        take(1),
        tap(runningPayroll => {
          this.store.dispatch(
            runningCPActions.loadRunningPayroll({
              payPeriodStart: runningPayroll.payPeriodStart,
              payPeriodEnd: runningPayroll.payPeriodEnd,
              belongsTo: runningPayroll.belongsTo,
              payoutFrequency: runningPayroll.payoutFrequency
            })
          )
        })
      )
      .subscribe()
  }

  ngOnDestroy(): void {
    this.destroy$.next()
    this.destroy$.complete()
  }

  private initializeBusinessEntityObservable(): void {
    this.businessEntity$ = combineLatest([
      this.runningPayroll$.pipe(filter((rp): rp is IncomingRunningPayrollBatch => !!rp)),
      this.store
        .select(selectBusinesses)
        .pipe(filter(businesses => !!businesses && businesses.length > 0))
    ]).pipe(
      map(([runningPayroll, businesses]) => {
        this.currentIncomingPayrolls = [
          ...runningPayroll.contractorPayrolls.fixed,
          ...runningPayroll.contractorPayrolls.hourly
        ]
        this.payPeriodStartDate = runningPayroll.payPeriodStart
        this.payPeriodEndDate = runningPayroll.payPeriodEnd
        return businesses.find(b => b.id === runningPayroll.belongsTo)?.businessName
      }),
      takeUntil(this.destroy$)
    )
  }

  // Helper to get employee name initials
  getInitials(firstName = '', lastName = ''): string {
    const firstInitial = firstName.charAt(0)
    const lastInitial = lastName.charAt(0)
    return (firstInitial + lastInitial).toUpperCase()
  }

  //onPageChange(newPage: number): void {}

  openModal(modal: unknown, index: number) {
    const modalRef = this.modalService.open(modal, {
      scrollable: false,
      centered: true,
      size: 'lg'
    })
    if (modalRef.componentInstance) {
      modalRef.componentInstance.payrollData = this.currentIncomingPayrolls[index]
      modalRef.componentInstance.payPeriodStartDate = this.payPeriodStartDate
      modalRef.componentInstance.payPeriodEndDate = this.payPeriodEndDate
    }
  }

  // confirmPayroll() {
  //   const data: { id: string; status: string; payrollIds: string[] } = {
  //     id: this.currentIncomingPayrolls[0].batchId as string,
  //     status: ContractorPayrollStatus.PROCESSED,
  //     payrollIds: []
  //   }

  //   this.currentIncomingPayrolls.forEach(payroll => {
  //     data.payrollIds.push(payroll.id as string)
  //   })

  //   this.store.dispatch(runningCPActions.updateRunningPayrollStatus({ ...data }))
  //   this.changeScreen(true)
  // }

  //gps 14 april 2026
  confirmPayroll() {
    const payrollIds = this.currentIncomingPayrolls
      .map(p => p.id)
      .filter((id): id is string => typeof id === 'string')

    if (payrollIds.length !== this.currentIncomingPayrolls.length) {
      throw new Error('Some payrolls are missing IDs. Cannot confirm payroll.')
    }

    const batchId = this.currentIncomingPayrolls[0]?.batchId

    if (!batchId) {
      throw new Error('Batch ID is missing')
    }

    const data = {
      id: batchId,
      status: ContractorPayrollStatus.PROCESSED,
      payrollIds
    }

    this.store.dispatch(runningCPActions.updateRunningPayrollStatus(data))
    this.changeScreen(true)
  }
}
